import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { FormattedMessage } from 'react-intl';
import { BodyText, SmallText } from '../typography';
import Button from '../ui/button';
import DocumentCameraSC from './document-camera.styled';

const DocumentCamera = ({ onCapture, documentType, isCapturing, hideFileUpload = false }) => {
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    setIsStreamActive(false);
  }, []);

  const startCamera = useCallback(() => {
    setIsStreamActive(true);
    setError(null);
  }, []);

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        fetch(imageSrc)
          .then(res => res.blob())
          .then(blob => {
            setCapturedImage(imageSrc);
            onCapture(blob, documentType);
            stopCamera();
          });
      }
    }
  }, [onCapture, documentType, stopCamera]);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    if (file && file.type && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setCapturedImage(imageUrl);
      onCapture(file, documentType);
      // reset to allow re-selecting same file
      event.target.value = '';
    }
  }, [onCapture, documentType]);

  const handleFileButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    if (capturedImage && capturedImage.startsWith('blob:')) {
      URL.revokeObjectURL(capturedImage);
    }
    // Resetujeme aj state v parent komponente
    onCapture(null, documentType);
  }, [capturedImage, onCapture, documentType]);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: documentType === 'selfie' ? 'user' : 'environment' // Predná kamera pre selfie, zadná pre dokumenty
  };

  return (
    <DocumentCameraSC>
      <div className="document-type-header">
        <BodyText className="document-type">
          <FormattedMessage
            id={`id_card_${documentType}`}
            defaultMessage={
              documentType === 'front'
                ? 'Predná strana OP'
                : documentType === 'back'
                ? 'Zadná strana OP'
                : 'Selfie fotografia'
            }
          />
        </BodyText>
      </div>

      {error && (
        <div className="error-message">
          <SmallText className="error">{error}</SmallText>
        </div>
      )}

      {!capturedImage && !isStreamActive && (
        <div className="camera-controls">
          <div className="upload-options">
            <Button type="PRIMARY" htmlType="button" onClick={startCamera} disabled={isCapturing}>
              <FormattedMessage
                id="document_camera_start"
                defaultMessage="Spustiť kameru"
              />
            </Button>

            {!hideFileUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileUpload}
                  className="file-input-hidden"
                  disabled={isCapturing}
                  style={{ display: 'none' }}
                />
                <Button htmlType="button" className="upload-white" onClick={handleFileButtonClick} disabled={isCapturing}>
                  <FormattedMessage
                    id="document_camera_upload"
                    defaultMessage="Nahrať zo súborov"
                  />
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {isStreamActive && (
        <div className="camera-view">
          <div className="video-container">
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="video-stream"
            />
          </div>

          <div className="camera-controls">
            <Button type="PRIMARY" htmlType="button" onClick={capturePhoto} disabled={isCapturing}>
              <FormattedMessage
                id="document_camera_capture"
                defaultMessage="Odfotiť"
              />
            </Button>
            <Button type="SECONDARY" htmlType="button" onClick={stopCamera}>
              <FormattedMessage
                id="document_camera_cancel"
                defaultMessage="Zrušiť"
              />
            </Button>
          </div>
        </div>
      )}

      {capturedImage && (
        <div className="captured-image">
          <img src={capturedImage} alt="Captured document" className="document-preview" />
          <div className="image-controls">
            <Button type="SECONDARY" htmlType="button" onClick={retakePhoto} disabled={isCapturing}>
              <FormattedMessage
                id="document_camera_retake"
                defaultMessage="Odfotiť znovu"
              />
            </Button>
          </div>
        </div>
      )}
    </DocumentCameraSC>
  );
};

export default DocumentCamera;
